import * as _ from "lodash"

import { normalizeName } from "../../searchprotocol"
import { parse, ParsedElement } from "./parsing"
import { INormalizedNameLUT, ITag, ITagModifier } from "../../index"

export const tagEqual = (a: ITag, b: ITag): boolean => {
    return _.isEqual(_.omitBy(a, _.isUndefined), _.omitBy(b, _.isUndefined))
}

export const parseModifiers = (inputValue: string): ITagModifier => {
    // Check for excluded term
    if (!!inputValue && inputValue[0] === "-") {
        // remove the "-" from the field
        const inputStripped = inputValue.slice(1)
        return {
            inputStripped,
            include: false,
            exclude: true,
        }
    }
    // Check for included term
    if (!!inputValue && inputValue[0] === "+") {
        // remove the "+" from the field
        const inputStripped = inputValue.slice(1)
        return {
            inputStripped,
            include: true,
            exclude: false,
        }
    }
    return {
        inputStripped: inputValue,
        include: false,
        exclude: false,
    }
}

export const convertStringToTag = (
    normalizedNameLUT: INormalizedNameLUT,
    input: string,
): ITag => {
    const { field, value } = splitAtColon(input)

    if (!!field) {
        const { inputStripped, include, exclude } = parseModifiers(field)

        const normalizedName = normalizeName(inputStripped)
        const fieldInfo = !!normalizedName && normalizedNameLUT[normalizedName]
        if (!!fieldInfo) {
            return {
                field: fieldInfo.original,
                value,
                input,
                fieldType: fieldInfo.fieldType,
                include,
                exclude,
            }
        } else {
            return {
                field: inputStripped,
                value,
                input,
                fieldType: undefined,
                include,
                exclude,
            }
        }
    } else {
        return {
            value,
            input,
            fieldType: undefined,
        }
    }
}

export const convertStringToTags = (
    normalizedNameLUT: INormalizedNameLUT,
    searchInputText: string,
): ITag[] => {
    const parsedElements = parse(searchInputText, normalizedNameLUT)

    const tags = convertParsedElementsToTags(parsedElements, normalizedNameLUT)

    return tags
}

export const restoreTagToString = (tag: ITag): string => tag.input

interface SplitResult {
    field?: string
    value: string
}

export const splitAtColon = (string: string): SplitResult => {
    string = string.trim()
    if (string.includes(":") && !/^"(.*)"$/.test(string)) {
        const colonIndex = string.indexOf(":")
        const [field, text] = [
            string.slice(0, colonIndex),
            string.slice(colonIndex + 1),
        ]
        return { field: field.trim(), value: text.trim() }
    } else {
        return { field: undefined, value: string }
    }
}

export const convertParsedElementsToTags = (
    parsedElements: ParsedElement[],
    normalizedNameLUT: INormalizedNameLUT,
): ITag[] => {
    const tags = parsedElements.flatMap((field) => {
        if (field.type === "keyValue") {
            const firstChar = field.key[0]
            const hasPrefix = firstChar === "+" || firstChar === "-"
            const elementKey = field.key.slice(hasPrefix ? 1 : 0)
            return {
                input: field.asString,
                field: elementKey,
                value: field.value,
                fieldType:
                    normalizedNameLUT[normalizeName(elementKey)].fieldType,
                exclude: firstChar === "-",
                include: firstChar === "+",
            } as ITag
        } else if (field.type === "text") {
            const plainText = field.asString
            return { input: plainText, value: plainText } as ITag
        } else {
            return []
        }
    })
    return tags
}
